// Depth-fighting ("z-fighting") repair for the Synty pieces.
//
// Several pieces carry the same face twice on the same plane — a ceiling layered twice, a wall's inner and outer skin, a roof's two layers a
// millimetre apart. The game draws them double-sided, so the GPU cannot decide which one is in front and the surface flickers into stripes
// (the "artifact flashing" on roofs and facades). Nothing is wrong with the pack — it is built for a 1 : 1 Unity camera — but at our scale
// the layers are closer together than the depth buffer can tell apart.
//
//   separateCoplanar(geometry, { scale })  → { geo, moved }  the same geometry, with each duplicated face moved ~2 mm (at source scale) off
//                                            the one beneath it along its own normal. `geo` is the input when nothing needed moving
//                                            (and running it on its own output moves nothing more).
//   fixGltfScene(root)                     → fixes every mesh of a loaded glTF scene in place; returns how many faces were moved.
//
// Layers already further apart than TOL are left alone; layers closer than EPS are pushed apart to EPS, keeping whichever was on top on top
// (exact duplicates: the smaller face — a detail on a wall — goes on top; equal faces: the later one). Only the faces concerned are
// touched, and only on pieces that have such pairs (those are made non-indexed so a moved face does not drag its neighbours).

import * as THREE from 'three'

export const DEPTH_EPS_M = 0.002 // faces on the same plane end up at least this far apart (metres at source scale)
export const DEPTH_TOL_M = 0.004 // …if they were closer than this (anything already further apart is fine)
const EXACT_M = 0.0001           // closer than this = the same plane exactly

const area2 = (poly) => { let a = 0; for (let i = 0; i < poly.length; i++) { const p = poly[i], q = poly[(i + 1) % poly.length]; a += p[0] * q[1] - q[0] * p[1] } return a / 2 }
// Overlap of two convex polygons (Sutherland–Hodgman); returns the clipped polygon.
function clip(subject, clipper) {
  let out = subject; const ccw = area2(clipper) > 0
  for (let i = 0; i < clipper.length && out.length; i++) {
    const A = clipper[i], B = clipper[(i + 1) % clipper.length]
    const inside = (P) => ((B[0] - A[0]) * (P[1] - A[1]) - (B[1] - A[1]) * (P[0] - A[0])) * (ccw ? 1 : -1) >= -1e-12
    const inter = (P, Q) => { const den = (P[0] - Q[0]) * (A[1] - B[1]) - (P[1] - Q[1]) * (A[0] - B[0]); const t = ((P[0] - A[0]) * (A[1] - B[1]) - (P[1] - A[1]) * (A[0] - B[0])) / den; return [P[0] + t * (Q[0] - P[0]), P[1] + t * (Q[1] - P[1])] }
    const inp = out; out = []
    for (let k = 0; k < inp.length; k++) { const P = inp[k], Q = inp[(k + 1) % inp.length]; if (inside(Q)) { if (!inside(P)) out.push(inter(P, Q)); out.push(Q) } else if (inside(P)) out.push(inter(P, Q)) }
  }
  return out
}

export function separateCoplanar(geo, { scale = 1, eps = DEPTH_EPS_M, tol = DEPTH_TOL_M } = {}) {
  const pos = geo?.attributes?.position
  if (!pos) return { geo, moved: 0 }
  const idx = geo.index
  const n = Math.floor((idx ? idx.count : pos.count) / 3)
  const k = 1 / Math.max(1e-9, scale)       // local units per metre
  const epsL = eps * k, tolL = tol * k, exactL = EXACT_M * k
  const tris = []
  const P = (i) => new THREE.Vector3().fromBufferAttribute(pos, idx ? idx.getX(i) : i)
  for (let t = 0; t < n; t++) {
    const v = [P(t * 3), P(t * 3 + 1), P(t * 3 + 2)]
    const nrm = new THREE.Vector3().subVectors(v[1], v[0]).cross(new THREE.Vector3().subVectors(v[2], v[0]))
    const len = nrm.length(); if (len < 1e-12) continue
    nrm.divideScalar(len)
    let sgn = 1, cn = nrm
    if (nrm.x < -1e-6 || (Math.abs(nrm.x) <= 1e-6 && (nrm.y < -1e-6 || (Math.abs(nrm.y) <= 1e-6 && nrm.z < 0)))) { sgn = -1; cn = nrm.clone().negate() } // canonical direction, so opposite faces compare as parallel
    tris.push({ t, v, n: nrm, cn, sgn, cd: cn.dot(v[0]), area: len / 2, off: 0 })
  }
  const order = tris.map((_, i) => i).sort((a, b) => tris[a].cd - tris[b].cd)
  const basisCache = new Map()
  const pairs = [] // { top, under, have } (same facing) | { A, B, front } (back to back)
  for (let oi = 0; oi < order.length; oi++) {
    const A = tris[order[oi]]
    for (let oj = oi + 1; oj < order.length; oj++) {
      const B = tris[order[oj]]
      if (B.cd - A.cd > tolL) break
      if (A.cn.dot(B.cn) < 0.9999) continue
      // 2-D overlap on the plane
      let bs = basisCache.get(A); if (!bs) { const u = Math.abs(A.cn.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0); const e1 = new THREE.Vector3().crossVectors(A.cn, u).normalize(); bs = [e1, new THREE.Vector3().crossVectors(A.cn, e1).normalize()]; basisCache.set(A, bs) }
      const ov = Math.abs(area2(clip(A.v.map((p) => [p.dot(bs[0]), p.dot(bs[1])]), B.v.map((p) => [p.dot(bs[0]), p.dot(bs[1])]))))
      if (ov < 0.05 * Math.min(A.area, B.area)) continue // they only touch, or overlap by a sliver
      const lo = A.t < B.t ? A : B, hi = lo === A ? B : A  // lo = earlier face in the file
      const along = (B.cd - A.cd) * A.sgn                  // how far B lies in front of A along A's facing direction
      if (A.sgn === B.sgn) {
        // same facing: keep the one already on top on top; exact duplicates → the smaller (a detail) on top, equal → the later
        let top, under
        if (Math.abs(along) <= exactL) { const eq = Math.abs(A.area - B.area) <= 0.05 * Math.max(A.area, B.area); [top, under] = eq ? [hi, lo] : (A.area < B.area ? [A, B] : [B, A]) } else [top, under] = along > 0 ? [B, A] : [A, B]
        pairs.push({ top, under, have: Math.abs(along) })
      } else { // back to back: A in front of B along A's normal is the right way round
        pairs.push({ A, B, front: -along })
      }
    }
  }
  const slack = epsL * 0.05 // (positions are 32-bit floats: a gap of 1.9 mm counts as 2 mm, so a second run finds nothing to do)
  for (let pass = 0; pass < 4; pass++) { // (a face may sit in several pairs: settle until nothing needs to move any more)
    let changed = false
    for (const p of pairs) {
      if (p.top) { const need = p.under.off + epsL - p.have; if (need > p.top.off + slack) { p.top.off = need; changed = true } }
      else { const need = (epsL - (p.front + p.A.off + p.B.off)) / 2; if (need > slack) { p.A.off += need; p.B.off += need; changed = true } }
    }
    if (!changed) break
  }
  const moving = tris.filter((T) => T.off > 0)
  if (!moving.length) return { geo, moved: 0 }
  const out = idx ? geo.toNonIndexed() : geo.clone()
  const op = out.attributes.position
  for (const T of moving) for (let c = 0; c < 3; c++) { const i = T.t * 3 + c; op.setXYZ(i, op.getX(i) + T.n.x * T.off, op.getY(i) + T.n.y * T.off, op.getZ(i) + T.n.z * T.off) }
  op.needsUpdate = true
  out.computeBoundingBox(); out.computeBoundingSphere()
  return { geo: out, moved: moving.length }
}

// Every mesh of a loaded glTF scene (the node's world scale decides what "2 mm" is). Returns the number of faces moved.
export function fixGltfScene(root) {
  let moved = 0
  root.updateWorldMatrix(true, true)
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry || o.userData.depthFixed) return
    const r = separateCoplanar(o.geometry, { scale: o.matrixWorld.getMaxScaleOnAxis() || 1 })
    o.userData.depthFixed = true
    if (r.moved) { o.geometry = r.geo; moved += r.moved }
  })
  return moved
}
